import { zodResolver } from '@hookform/resolvers/zod'
import { ColumnDef } from '@tanstack/react-table'
import to from 'await-to-js'
import { ActionButtons } from 'components/Buttons/ActionButtons'
import dialogConfirm from 'components/dialogConfirm'
import { Cuisine50Options } from 'models/cuisine_50'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import apiService from 'services/api'
import { z } from 'zod'
import FbButton from 'components/FbUI/FbButton'
import { Plus } from 'lucide-react'

import { DataTable, DataTableProvider } from 'components/DataTable'
import { IColumnVisibility } from 'components/DataTable/types'
import { DataTableContainer } from 'components/DataTable/UI'
import { useDataTableContext } from 'components/DataTable/DataTableContext'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { ProductModal } from 'components/ProductFlow/ProductModal'
import {
  formatDateWithTimeAndMinutes,
  formatPercentage,
  formatTwoDecimal,
  formatUsdDecimal,
} from 'utils/formatting'
import { Product, Variant } from 'models/product'
import { handleFormError } from 'utils/handleFormError'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { TableSearch } from 'components/Filters/TableSearch'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import ProductsFilterset from './ProductsFilterset'
import VariantsFilterset from './VariantsFilterset'
import * as S from '../CommonTable.styles'
import * as SettingsS from 'features/settings/components/styles'
import { FilterIdentifier } from 'models/saved_view'
import { getTableQueryKey } from 'utils/getTableQueryKey'
import { useQueryClient } from '@tanstack/react-query'
import { featureFlagService } from 'utils/featureFlagService'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import { BsDownload } from 'react-icons/bs'
import { IconMiniButton } from 'components/FbUI/FbButton'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Tabs } from 'components/Tabs/Tabs'
import { VariantSearchTable } from 'components/Tables/VariantSearchTable/VariantSearchTable'
import {
  useGetVariantsCatalog,
  useGetProductsCatalog,
  exportProductsCatalog,
  exportProductVariantsCatalog,
  useProductsViewModeStore,
  useGetSyndigoSyncStatus,
  getStatusColor,
} from 'services/products_api'
import { StandaloneVariantModal } from 'components/ProductFlow/StandaloneVariantModal'
import { PillListCell } from 'components/Tables/CommonTableCells/PillListCell'
import { invalidateProductQueries } from 'utils/productQueryInvalidation'
import { downloadFile } from 'utils/csvDownloader'
import { toast } from 'react-toastify'

export interface ProductFormValues {
  name: string
  format: string
  product_format: string
  price_per_case: number
  mfg_avg_list_price_per_lb: number
  avg_serving_size_per_lb: number
  est_monthly_churn_rate: number
  value_per_consumer_new_trial: number
  lbs_per_case: number
  current_aided_awareness: number
  current_national_trial_perc: number
  value_per_consumer_new_informed: number
  estimated_sales_potential: Record<string, number>
  unit: string
  variant_count?: number
  variant_names?: string[]
  variants?: Variant[]
}

export const productShape = z.object({
  name: z.string().min(1, 'Name is required').max(60, 'Name is too long'),
  // format: z.string().optional(), TODO: Do we still need this field?
  // product_format: z.string().min(1, 'Product format is required'), TODO: Do we still need this field?
  avg_serving_size_per_lb: z
    .number({ invalid_type_error: 'Average serving size is required' })
    .gt(0, 'Average serving size must be greater than 0'),
  mfg_avg_list_price_per_lb: z
    .number({ invalid_type_error: 'Average revenue per unit is required' })
    .gt(0, 'Average revenue must be greater than 0'),
  est_monthly_churn_rate: z
    .number({ invalid_type_error: 'Monthly churn rate is required' })
    .gt(0, 'Churn rate must be greater than 0')
    .max(100, 'Churn rate cannot exceed 100%')
    .default(1),
  // Unit field is UI-only for display purposes (e.g., showing "lbs" suffix).
  // Not persisted to backend - excluded from API payload.
  unit: z.string().optional(),
  value_per_consumer_new_trial: z
    .number()
    .min(0, 'Value cannot be negative')
    .nullable()
    .optional(),
  lbs_per_case: z
    .number()
    .min(0, 'Serving size cannot be negative')
    .nullable()
    .optional(),
  current_aided_awareness: z
    .number()
    .min(0, 'Awareness cannot be negative')
    .max(100, 'Awareness cannot exceed 100%')
    .nullable()
    .optional(),
  current_national_trial_perc: z
    .number()
    .min(0, 'Trial percentage cannot be negative')
    .max(100, 'Trial percentage cannot exceed 100%')
    .nullable()
    .optional(),
  variant_count: z.number().optional(),
  variant_names: z.array(z.string()).optional(),
  variants: z
    .array(
      z
        .object({
          id: z.number().optional().nullable(),
          name: z.string().min(1, 'Name is required'),
          description: z.string().optional().nullable(),
          gtin: z.string().nullable(),
          sku: z.string().nullable(),
          units_per_case: z
            .union([z.number(), z.literal('')])
            .optional()
            .nullable(),
          case_weight: z
            .union([z.number(), z.literal('')])
            .optional()
            .nullable(),
          unit_weight: z
            .union([z.number(), z.literal('')])
            .optional()
            .nullable(),
          format_pack_size: z.string().nullable(),
          distributor_codes: z
            .array(
              z.object({
                id: z.number().optional(),
                distributor_name: z
                  .string()
                  .min(1, 'Distributor name is required'),
                distributor_code: z
                  .string()
                  .min(1, 'Distributor code is required'),
              })
            )
            .optional(),
        })
        .partial()
    )
    .optional()
    .nullable(),
})

const espValidationSchema = z.object({
  default_sales: z
    .number({ invalid_type_error: 'Default sales value is required' })
    .min(0, 'Default sales cannot be negative'),
  cuisine_sales: z.record(
    z.string(),
    z.number().min(0, 'Sales estimates cannot be negative')
  ),
})

const defaultValues: ProductFormValues = {
  name: 'New Product', // Valid default - user will replace
  format: '',
  product_format: '',
  price_per_case: 0,
  mfg_avg_list_price_per_lb: 1, // Valid default value
  avg_serving_size_per_lb: 1, // Valid default value
  est_monthly_churn_rate: 4,
  value_per_consumer_new_trial: 0,
  lbs_per_case: 0,
  current_aided_awareness: 0,
  current_national_trial_perc: 0,
  value_per_consumer_new_informed: 0,
  estimated_sales_potential: {},
  unit: 'lbs',
  variant_count: 0,
  variant_names: [],
  variants: [],
}

const defaultESPValues: Product['estimated_sales_potential'] = {}
Cuisine50Options.forEach((cuisine) => {
  defaultESPValues[cuisine.value] = 0
})

const PAGE_SIZE = 100
const IDENTIFIER = 'PRODUCTS_TABLE'
const VARIANTS_IDENTIFIER = 'VARIANTS_TABLE'

type SortableFields = (keyof Product | string)[]

// Sortable fields for product catalog (includes variant_count)
const sortableFields: SortableFields = [
  'name',
  'format',
  'avg_serving_size_per_lb',
  'mfg_avg_list_price_per_lb',
  'est_monthly_churn_rate',
  'estimated_sales_potential.default_sales',
  'created',
  'modified',
  'variant_count',
]

interface ProductTableProps {
  tableKey: string
}

const productDescriptionText = `
  Current LTV Drivers: Cuisine, review composition, cross-platform ratings, menu dynamics,
  expense quartiles, chain size, demographics, population density, repeater rate, awareness effect,
  user inputs (churn, volume / unit, etc)
  Allow up to an hour for LTV to calculate when updated.
  `

function ProductTableComponent({ tableKey }: ProductTableProps) {
  const [step, setStep] = useState(0)
  const api = apiService()
  const queryClient = useQueryClient()
  const { enableSyndigoIntegration, isLoading: featureFlagsLoading } =
    featureFlagService()

  const { data: syndigoSyncStatus } = useGetSyndigoSyncStatus()

  // View mode toggle
  const { viewMode, setViewMode } = useProductsViewModeStore()

  const productsFilterStore = filterStoreRepo.getStore(
    IDENTIFIER as FilterIdentifier
  )
  const { page, setPage } = productsFilterStore()
  const sortingStore = dataTableSortingStoreRepo.getStore(tableKey)
  const { sorting } = sortingStore()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showVariantModal, setShowVariantModal] = useState<boolean>(false)
  const [selectedProductID, setSelectedProductID] = useState<number>()

  const filtersAsParams = useFilterParams(productsFilterStore)
  const { preferences, isLoading: isPreferencesLoading } = usePreferences()

  // Table context for row selection
  const tableContext = useDataTableContext()
  const {
    state: { rowSelection, totalSelectedRows, isAllRowsSelected },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = tableContext

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      const sortField = sorting[0]?.id
      // Validate sort field is supported by current endpoint
      if (sortableFields.includes(sortField)) {
        params['sort'] = sorting[0].desc ? '-' + sortField : sortField
      }
    }

    return params
  }, [sorting, sortableFields])

  const filterAndSortParams = useMemo(
    () => ({
      ...filtersAsParams,
      ...sortParams,
    }),
    [filtersAsParams, sortParams]
  )

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: tableKey,
    filterParams: filterAndSortParams,
    page: page,
  })

  // DATA FETCHING
  const {
    isLoading: isProductsLoading,
    data,
    refetch,
  } = useGetProductsCatalog({
    params: {
      ...filterAndSortParams,
      page: page,
      limit: PAGE_SIZE,
    },
    enabled: !featureFlagsLoading,
    refetchOnMount: 'always',
    customQueryKey: TABLE_QUERY_KEY,
  })
  // END DATA FETCHING

  // Fetch variants data when in variants mode (just for count)
  const variantsFilterStore = filterStoreRepo.getStore(
    VARIANTS_IDENTIFIER as FilterIdentifier
  )
  const variantsFiltersAsParams = useFilterParams(variantsFilterStore)

  const { data: variantsData } = useGetVariantsCatalog({
    filters: variantsFiltersAsParams,
    page: 1,
    page_size: 1, // Just need the count, not the data
    enabled: viewMode === 'variants',
  })

  // Row selection helpers
  const products = data?.results ?? []

  const selectedProductIds = useMemo(() => {
    return (
      products
        ?.filter((_: Product, i: number) => rowSelection[i])
        ?.map((p: Product) => p.id) ?? []
    )
  }, [products, rowSelection])

  const unselectedProductIds = useMemo(() => {
    if (!isAllRowsSelected) return []
    return (
      products
        ?.filter((_: Product, i: number) => !rowSelection[i])
        ?.map((p: Product) => p.id) ?? []
    )
  }, [products, rowSelection, isAllRowsSelected])

  // Update total rows for "select all" functionality
  useEffect(() => {
    if (data?.count) {
      setTotalRowsInBackend(data.count)
    }
  }, [data?.count, setTotalRowsInBackend])

  const handleExport = async () => {
    if (viewMode === 'products') {
      await handleExportProducts()
    } else {
      await handleExportProductVariants()
    }
  }

  const handleExportProducts = async () => {
    try {
      // All available columns for export
      const columns = [
        'name',
        'product_format',
        'variant_count',
        'mfg_avg_list_price_per_lb',
        'avg_serving_size_per_lb',
        'est_monthly_churn_rate',
        'estimated_sales_potential',
        'created',
        'modified',
      ]

      const params: Record<string, any> = {
        ...filterAndSortParams,
        include_ids: isAllRowsSelected ? undefined : selectedProductIds,
        exclude_ids: isAllRowsSelected ? unselectedProductIds : undefined,
      }

      const response = await exportProductsCatalog(params, {
        selected_columns: columns,
        export_type: 'csv',
      })

      downloadFile(response.data, 'products_export.csv')
      clearSelectedRows()
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export products. Please try again.')
    }
  }

  const handleExportProductVariants = async () => {
    try {
      // All available columns for variants export
      const columns = [
        'name',
        'sku',
        'gtin',
        'description',
        'product_name',
        'match_status',
        'format_pack_size',
        'units_per_case',
        'case_weight',
        'case_weight_unit',
        'unit_weight',
        'unit_weight_unit',
        'avg_price',
        'created',
        'modified',
      ]

      const variantsSortingStore = dataTableSortingStoreRepo.getStore(
        `${tableKey}-variants`
      )
      const variantsSorting = variantsSortingStore.getState().sorting

      const variantsSortParams: Record<string, unknown> = {}
      if (variantsSorting?.length) {
        const sortField = variantsSorting[0]?.id
        variantsSortParams['sort'] = variantsSorting[0].desc
          ? '-' + sortField
          : sortField
      }

      const params: Record<string, any> = {
        ...variantsFiltersAsParams,
        ...variantsSortParams,
      }

      const response = await exportProductVariantsCatalog(params, {
        selected_columns: columns,
        export_type: 'csv',
      })

      downloadFile(response.data, 'variants_export.csv')
      clearSelectedRows()
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export variants. Please try again.')
    }
  }

  const reportingUnit = preferences?.reporting_unit || 'lbs'
  const isLoading =
    isProductsLoading || isPreferencesLoading || featureFlagsLoading

  const espForm = useForm({
    defaultValues: defaultESPValues,
    resolver: zodResolver(espValidationSchema),
    mode: 'onChange',
  })

  const productForm = useForm<ProductFormValues>({
    resolver: zodResolver(productShape),
    mode: 'all', // Validate on mount, change, blur, and submit
    defaultValues: {
      ...defaultValues,
      unit: reportingUnit,
    },
    shouldUseNativeValidation: false,
    criteriaMode: 'all',
  })

  const onErrorGoToRightStep = (error: Record<string, any>) => {
    const firstStepFields = [
      'name',
      'avg_serving_size_per_lb',
      'mfg_avg_list_price_per_lb',
      'est_monthly_churn_rate',
      'value_per_consumer_new_trial',
      'lbs_per_case',
      'current_aided_awareness',
      'current_national_trial_perc',
    ]
    const secondStepFields = ['default_sales', 'cuisine_sales']
    const errorKeys = Object.keys(error)
    if (errorKeys.some((key) => firstStepFields.includes(key))) {
      setStep(0)
    } else if (errorKeys.some((key) => secondStepFields.includes(key))) {
      setStep(1)
    }
  }

  const onSubmitCreate = productForm.handleSubmit(async (values) => {
    let esp = {} as Product['estimated_sales_potential']
    await espForm.handleSubmit((data) => (esp = data), console.warn)()

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { unit: _unit, ...valuesWithoutUnit } = values

    // unit field is no longer saved and validated product-by-product and is a gloabl user preference
    const payload = {
      ...valuesWithoutUnit,
      format: values.product_format,
      estimated_sales_potential: esp,
      variants: values.variants || [],
    }

    const [err] = await to(api.postProduct(payload))
    if (err) {
      handleFormError(err, productForm.setError)
      onErrorGoToRightStep(err)
      return
    }

    await refetch()

    // Invalidate all product/variant queries
    invalidateProductQueries(queryClient)

    hideModal()
  })

  const onSubmitUpdate = async () => {
    const values = productForm.getValues()
    let esp = {} as Product['estimated_sales_potential']
    await espForm.handleSubmit((data) => (esp = data), console.warn)()

    if (!selectedProductID) return

    Object.keys(esp).forEach((key) => {
      const curValue = esp[key]
      esp[key] = curValue ? curValue : 0
    })

    // Prepare update payload
    const updatePayload: any = {
      ...values,
      format: values.product_format,
      estimated_sales_potential: esp,
      // Always include variants array (even if empty) to be explicit
      variants: values.variants || [],
      // Always skip variant deletion (new catalog behavior)
      skip_variant_deletion: true,
    }

    const [err] = await to(api.putProduct(selectedProductID, updatePayload))
    if (err) {
      handleFormError(err, productForm.setError)
      onErrorGoToRightStep(err)
      return
    }

    await refetch()

    // Invalidate all product/variant queries
    invalidateProductQueries(queryClient)

    hideModal()
  }

  const columns = useMemo<ColumnDef<Product, any>[]>(() => {
    const allColumns: ColumnDef<Product, any>[] = [
      {
        accessorKey: 'name',
        header: () => '',
        enableHiding: false,
        meta: {
          headerTitle: 'Product Name',
          isFirstColumn: true,
          isEditable: false,
        },
        size: 200,
      },
      {
        accessorKey: 'avg_serving_size_per_lb',
        header: 'Avg. Volume per Unit',
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          return value ? `${formatTwoDecimal(value)} ${reportingUnit}` : '-'
        },
      },
      {
        accessorKey: 'mfg_avg_list_price_per_lb',
        header: 'Avg. Revenue per Unit',
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          return value ? formatUsdDecimal(info.getValue()) : '-'
        },
      },
      {
        accessorKey: 'est_monthly_churn_rate',
        header: 'Est. Monthly Churn Rate',
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          return value ? `${formatPercentage(info.getValue())} %` : '-'
        },
      },
      {
        accessorKey: 'estimated_sales_potential.default_sales',
        header: 'Avg. Units Sold per Month',
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          return value
            ? `${formatTwoDecimal(value)} (${formatTwoDecimal(info.row.original.avg_serving_size_per_lb * value)} ${reportingUnit})`
            : '-'
        },
      },
      {
        id: 'variant_count',
        accessorKey: 'variant_count',
        header: 'Variants',
        size: 250,
        enableHiding: true,
        cell: (info) => {
          const row = info.row.original
          const variantNames = row.variant_names || []
          const totalCount = row.variant_count || 0

          // If no variants, show dash
          if (totalCount === 0 || variantNames.length === 0) {
            return <div className="text-gray-400">-</div>
          }

          // Show pills for first few, then "+X more"
          return (
            <PillListCell
              items={variantNames}
              maxColumnWidth={230}
              maxPillWidth={120}
              totalCount={totalCount}
            />
          )
        },
      },
      {
        id: 'edit',
        header: 'Actions',
        size: 100,
        enableHiding: false,
        meta: { rightAlign: true, isLastColumn: true, isEditable: false },
        cell: ({ row }) => (
          <ActionButtons
            className={'justify-end pr-0'}
            onDelete={() => handleRowDelete(row.index)}
            onEdit={() => handleRowEdit(row.index)}
          />
        ),
      },
    ]

    // Always include all columns (new catalog behavior)
    return allColumns
  }, [data?.results, reportingUnit])

  function hideModal() {
    setShowModal(false)
    productForm.reset({
      ...defaultValues,
      unit: reportingUnit,
    })
    espForm.reset(defaultESPValues)
    setSelectedProductID(undefined)
  }

  async function handleRowDelete(index: number) {
    const id = data?.results?.[index]?.id
    if (!id) return

    if (
      await dialogConfirm(
        'Are you sure you want to delete this product? This action will also delete any deals associated with the product.',
        'Delete Product'
      )
    ) {
      try {
        await api.deleteProduct(id)
      } catch {
        /* empty */
      }
      await refetch()
    }
  }

  function handleRowEdit(index: number) {
    const selectedProduct = data?.results?.find(
      (product: Product) => product.id === data?.results?.[index]?.id
    )

    if (!selectedProduct) return

    setShowModal(true)
    setSelectedProductID(data?.results?.[index]?.id)

    // Structure ESP form values
    const espInitialValues = {
      default_sales: selectedProduct.estimated_sales_potential.default_sales,
      cuisine_sales: selectedProduct.estimated_sales_potential.cuisine_sales,
    }

    espForm.reset(espInitialValues)
    if (data) {
      // For new catalog mode, ensure variants array is empty (for NEW variants only)
      // Existing variants are displayed via variant_names and variant_count
      productForm.reset({
        ...selectedProduct,
        variants: [], // Always start with empty array for new variants
      })
    }
  }

  return (
    <DataTableContainer>
      {/* Shared header for both views */}
      <div
        style={{
          height: 'calc(100% - 32px)',
        }}
      >
        {(() => {
          const sharedHeader = (
            <>
              <SettingsS.SettingsTableHeader>
                <SettingsS.TableDescriptionText>
                  <strong>First Bite LTV Version 1.4</strong>
                  <OverflownTextTooltip
                    tooltipText={productDescriptionText}
                    maxLines={4}
                    placement="bottom"
                  >
                    {productDescriptionText}
                  </OverflownTextTooltip>
                </SettingsS.TableDescriptionText>
              </SettingsS.SettingsTableHeader>

              {/* View Mode Toggle */}
              <div className="flex items-center justify-between w-full">
                <div className="mb-4 w-full max-w-[400px]">
                  <Tabs
                    tabs={[
                      {
                        key: 'products' as const,
                        title: 'Products',
                        badge: data?.count?.toLocaleString(),
                      },
                      {
                        key: 'variants' as const,
                        title: 'Product Variants',
                        badge: variantsData?.count?.toLocaleString(),
                      },
                    ]}
                    selectedTab={viewMode}
                    setSelectedTab={setViewMode}
                  />
                </div>

                <FbButton
                  onClick={() => {
                    // Open variant modal if in variants view, otherwise product modal
                    if (viewMode === 'products') {
                      setShowModal(true)
                    } else {
                      setShowVariantModal(true)
                    }
                  }}
                >
                  <Plus size={16} />
                  {viewMode === 'products'
                    ? 'Create Product'
                    : 'Create Variant'}
                </FbButton>
              </div>
            </>
          )

          // Show variants view or products view based on viewMode
          return viewMode === 'products' ? (
            <DataTable
              key={tableKey}
              tableHeader={
                <>
                  {sharedHeader}
                  <S.SearchContainer>
                    <S.SearchContainerFilters>
                      <S.TableSearchContainer>
                        <TableSearch
                          filterStore={productsFilterStore}
                          searchPlaceholder="Search by product name, variant name, SKU, or UPC/GTIN..."
                          tableKey={tableKey}
                        />
                      </S.TableSearchContainer>
                      <S.TableButtonsContainer>
                        {viewMode === 'products' ? (
                          <ProductsFilterset
                            filterIdentifier={IDENTIFIER as FilterIdentifier}
                          />
                        ) : (
                          <VariantsFilterset
                            filterIdentifier={
                              VARIANTS_IDENTIFIER as FilterIdentifier
                            }
                          />
                        )}
                        <ColumnSelectorRecipient tableKey={tableKey} />
                      </S.TableButtonsContainer>
                    </S.SearchContainerFilters>
                    <div className="flex flex-row justify-between gap-4">
                      <TableButtonAction
                        selectedRowsCount={totalSelectedRows}
                        entityType="products"
                        items={[
                          {
                            label: 'Export',
                            icon: <BsDownload size={20} />,
                            callback: handleExport,
                          },
                        ]}
                      />
                      <FilterChips
                        identifier={IDENTIFIER}
                        store={productsFilterStore}
                        clearAllButton
                        showActive
                      />
                    </div>
                  </S.SearchContainer>
                </>
              }
              tableKey={tableKey}
              loading={isLoading}
              data={data?.results ?? []}
              columns={columns}
              sortableFields={sortableFields}
              enableRowSelection={true}
              virtualizeRows={false}
              isPaginationEnabled={true}
              stickyLastColumn
              paginationOptions={{
                totalRows: data?.count ?? 0,
                pageSize: PAGE_SIZE,
                setPage: setPage,
                page: page,
                isPaginationLoading: isLoading,
              }}
              selectAllText="Select Products"
              footerControls={
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="tooltip-export">
                      {totalSelectedRows === 0
                        ? 'Export all products'
                        : `Export ${totalSelectedRows} selected product${totalSelectedRows !== 1 ? 's' : ''}`}
                    </Tooltip>
                  }
                >
                  <div>
                    <IconMiniButton onClick={handleExportProducts}>
                      <BsDownload size={18} />
                    </IconMiniButton>
                  </div>
                </OverlayTrigger>
              }
            />
          ) : (
            /* Variants View */
            <VariantSearchTable
              mode="standalone"
              tableKey={`${tableKey}-variants`}
              showFilters={true}
              tableHeader={sharedHeader}
              footerControls={
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="tooltip-export">
                      {totalSelectedRows === 0
                        ? 'Export all products variants'
                        : `Export ${totalSelectedRows} selected product variant${totalSelectedRows !== 1 ? 's' : ''}`}
                    </Tooltip>
                  }
                >
                  <div>
                    <IconMiniButton onClick={handleExportProductVariants}>
                      <BsDownload size={18} />
                    </IconMiniButton>
                  </div>
                </OverlayTrigger>
              }
            />
          )
        })()}
      </div>

      {enableSyndigoIntegration && syndigoSyncStatus && (
        <div className="flex gap-4 items-center text-sm mt-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-700">Syndigo Status:</span>
            <span
              className="px-3 py-1 rounded-full text-white font-medium capitalize"
              style={{
                backgroundColor: getStatusColor(syndigoSyncStatus.status),
              }}
            >
              {syndigoSyncStatus.status.replace('_', ' ')}
            </span>
          </div>
          <div className="h-6 w-px bg-gray-300" />
          <div className="text-gray-500">
            <strong>Last Sync:</strong>{' '}
            {syndigoSyncStatus.lastSyncAt
              ? formatDateWithTimeAndMinutes(syndigoSyncStatus.lastSyncAt)
              : 'Never'}
          </div>
          <div className="text-gray-500">
            <strong>Last Full Sync:</strong>{' '}
            {syndigoSyncStatus.lastFullSyncAt
              ? formatDateWithTimeAndMinutes(syndigoSyncStatus.lastFullSyncAt)
              : 'Never'}
          </div>
        </div>
      )}

      <ProductModal
        show={showModal}
        handleClose={hideModal}
        selectedProductID={selectedProductID}
        defaultValues={defaultValues}
        productForm={productForm}
        espForm={espForm}
        onSubmit={async () =>
          selectedProductID ? onSubmitUpdate() : onSubmitCreate()
        }
        step={step}
        setStep={setStep}
      />

      <StandaloneVariantModal
        show={showVariantModal}
        onHide={() => setShowVariantModal(false)}
      />
    </DataTableContainer>
  )
}

ProductTableComponent.displayName = 'ProductTable'

export function ProductTable(
  props: ProductTableProps & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const { tableKey, defaultColumnVisibility, ...rest } = props

  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      defaultColumnVisibility={defaultColumnVisibility}
      preferredGroupOrder={['Product Details', 'Product Metrics', 'Activity']}
    >
      <DataTableProvider tableKey={tableKey}>
        <ProductTableComponent tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
